import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Confidence } from "@/hooks/use-baseline";

export type ActionItemKind = "action" | "schedule" | "followup";
export type ActionItemStatus = "pending" | "sent" | "snoozed" | "dismissed" | "done";

export interface PmTarget {
  project: string;
  type: string;
  epic: string | null;
}

export interface ActionItemRow {
  id: string;
  solutionId: string;
  externalKey: string | null;
  sourceId: string;
  kind: ActionItemKind;
  status: ActionItemStatus;
  confidence: Confidence;
  title: string;
  quote: string | null;
  sourceSpeakerId: string | null;
  ownerUserId: string | null;
  dueDate: string | null;
  pmTarget: PmTarget | null;
  createdAt: string;
  updatedAt: string;
  source: { id: string; name: string; type: string } | null;
  sourceSpeaker: { id: string; name: string; initials: string } | null;
  owner: { id: string; name: string; email: string } | null;
}

export interface ActionItemPatchBody {
  status?: ActionItemStatus;
  title?: string;
  ownerUserId?: string | null;
  dueDate?: string | null;
  pmTarget?: PmTarget | null;
}

const actionItemKeys = {
  list: (solutionId: string) => ["action-items", "list", solutionId] as const,
} as const;

export function useActionItems(solutionId: string) {
  return useQuery({
    queryKey: actionItemKeys.list(solutionId),
    queryFn: async (): Promise<ActionItemRow[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/action-items`);
      if (!res.ok) throw new Error("Failed to fetch action items");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function usePatchActionItem(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ActionItemPatchBody }) => {
      const res = await fetch(`/api/action-items/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Failed to update action item");
      const json = await res.json();
      return json.data as ActionItemRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actionItemKeys.list(solutionId) }),
    onError: () => toast.error("Failed to update action item"),
  });
}
